import {
  Banknote,
  ChevronDown,
  ChevronUp,
  Plus,
  RefreshCw,
  UsersRound,
  Wifi,
  WifiOff,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { api, formatApiError } from "@/lib/api";


const today = new Date().toISOString().slice(0, 10);

const formatCurrency = (value) => new Intl.NumberFormat("id-ID", {
  style: "currency",
  currency: "IDR",
  maximumFractionDigits: 0,
}).format(value || 0);

const formatUpdatedAt = (value) => value
  ? new Intl.DateTimeFormat("id-ID", {
    dateStyle: "medium",
    timeStyle: "medium",
  }).format(new Date(value))
  : "Belum ada pembaruan";

export const ManagerDashboard = () => {
  const [overview, setOverview] = useState(null);
  const [profit, setProfit] = useState(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isManualRefresh, setIsManualRefresh] = useState(false);
  const [expandedTransactionCode, setExpandedTransactionCode] = useState(null);
  const inFlightRef = useRef(false);
  const refreshCycleRef = useRef(0);

  const loadDashboard = useCallback(async (isBackground = false) => {
    if (inFlightRef.current) {
      return;
    }

    inFlightRef.current = true;
    if (!isBackground) {
      setIsLoading(true);
      setIsManualRefresh(true);
    }

    try {
      const overviewRequest = api.get("/dashboard/overview", {
        params: { audit: !isBackground },
      });
      refreshCycleRef.current += 1;
      const shouldRefreshProfit = !isBackground || refreshCycleRef.current % 15 === 0;
      const profitRequest = shouldRefreshProfit
        ? api.get("/reports/profit", {
          params: { date_from: today, date_to: today },
        })
        : Promise.resolve(null);
      const [overviewResponse, profitResponse] = await Promise.all([
        overviewRequest,
        profitRequest,
      ]);
      setOverview(overviewResponse.data);
      if (profitResponse) {
        setProfit(profitResponse.data);
      }
      setError("");
    } catch (requestError) {
      if (!isBackground) {
        setError(formatApiError(requestError));
      }
    } finally {
      inFlightRef.current = false;
      if (!isBackground) {
        setIsLoading(false);
        setIsManualRefresh(false);
      }
    }
  }, []);

  useEffect(() => {
    loadDashboard();
    const refreshTimer = window.setInterval(() => loadDashboard(true), 1000);

    return () => window.clearInterval(refreshTimer);
  }, [loadDashboard]);

  const recentTransactions = useMemo(() => {
    if (!overview) {
      return [];
    }

    return overview.stores
      .flatMap((store) => store.transactions.map((transaction) => ({
        ...transaction,
        storeName: store.name,
      })))
      .sort((left, right) => right.transaction_time.localeCompare(left.transaction_time))
      .slice(0, 5);
  }, [overview]);

  const isOnline = Boolean(overview?.online_store_count);
  const statusLabel = overview?.is_complete
    ? "API ONLINE"
    : isOnline
      ? "DATA SEBAGIAN"
      : "API OFFLINE";
  const StatusIcon = isOnline ? Wifi : WifiOff;
  const hasProfitData = Boolean(profit?.is_complete);

  return (
    <main className="manager-dashboard" data-testid="manager-dashboard-page">
      <section className="manager-dashboard-inner">
        <header className="manager-header">
          <div>
            <p className="manager-brand" data-testid="manager-dashboard-brand">ANEKA / POS</p>
            <h1 data-testid="manager-dashboard-title">Laporan penjualan</h1>
            <p className="manager-store-name" data-testid="manager-store-name">
              {overview?.stores.map((store) => store.name).join(" · ") || "Cabang Anda"}
            </p>
          </div>
          <div className="manager-header-actions">
            <span
              className={`manager-status ${isOnline ? "is-online" : "is-offline"}`}
              data-testid="manager-api-status"
            >
              <StatusIcon aria-hidden="true" size={16} />
              {statusLabel}
            </span>
            <button
              className="manager-refresh-button"
              data-testid="manager-refresh-button"
              disabled={isManualRefresh}
              onClick={() => loadDashboard(false)}
              type="button"
            >
              <RefreshCw aria-hidden="true" className={isManualRefresh ? "spin" : ""} size={20} />
              Refresh
            </button>
          </div>
        </header>

        {error && <p className="form-error" data-testid="manager-dashboard-error" role="alert">{error}</p>}
        {isLoading && !overview && (
          <p className="loading-note" data-testid="manager-dashboard-loading">Memuat laporan kasir…</p>
        )}

        {overview && (
          <>
            <section className="manager-kpi-grid" aria-label="Ringkasan penjualan cabang">
              <article className="manager-kpi manager-sales-kpi" data-testid="manager-sales-card">
                <div><span>Total omzet hari ini</span><Banknote aria-hidden="true" size={20} /></div>
                <strong data-testid="manager-sales-value">{formatCurrency(overview.total_sales)}</strong>
              </article>
              <article className="manager-kpi" data-testid="manager-transactions-card">
                <div><span>Total transaksi hari ini</span></div>
                <strong className="manager-transaction-value" data-testid="manager-transactions-value">
                  <UsersRound aria-hidden="true" size={19} />
                  {overview.transaction_count.toLocaleString("id-ID")}
                </strong>
              </article>
              <article className="manager-kpi manager-profit-kpi" data-testid="manager-profit-card">
                <div><span>Total laba hari ini</span><Banknote aria-hidden="true" size={20} /></div>
                <strong
                  className={`manager-profit-value ${hasProfitData ? "has-value" : "is-pending"}`}
                  data-testid="manager-profit-value"
                >
                  {hasProfitData && <Plus aria-hidden="true" size={20} />}
                  {hasProfitData ? formatCurrency(profit.total_profit) : "Menunggu data"}
                </strong>
              </article>
            </section>

            <section className="manager-recent-section" aria-labelledby="manager-recent-title">
              <div className="manager-section-heading compact-heading">
                <div>
                  <p>HARI INI</p>
                  <h2 id="manager-recent-title">Transaksi terakhir</h2>
                </div>
                <span data-testid="manager-recent-count">{recentTransactions.length} TERBARU</span>
              </div>
              <div className="manager-recent-list" data-testid="manager-recent-transaction-list">
                {recentTransactions.map((transaction) => (
                  <div
                    className="manager-transaction-entry"
                    data-testid={`manager-transaction-${transaction.transaction_code}`}
                    key={transaction.transaction_code}
                  >
                    <button
                      aria-expanded={expandedTransactionCode === transaction.transaction_code}
                      className="manager-transaction-button"
                      data-testid={`toggle-transaction-${transaction.transaction_code}`}
                      onClick={() => setExpandedTransactionCode((current) => (
                        current === transaction.transaction_code ? null : transaction.transaction_code
                      ))}
                      type="button"
                    >
                      <span>{transaction.transaction_time} · {transaction.transaction_code}</span>
                      <strong>{formatCurrency(transaction.total_sales)}</strong>
                      {expandedTransactionCode === transaction.transaction_code
                        ? <ChevronUp aria-hidden="true" size={17} />
                        : <ChevronDown aria-hidden="true" size={17} />}
                    </button>
                    {expandedTransactionCode === transaction.transaction_code && (
                      <div
                        className="manager-item-detail-list"
                        data-testid={`transaction-items-${transaction.transaction_code}`}
                      >
                        {transaction.items.map((item, itemIndex) => (
                          <p
                            data-testid={`transaction-item-${transaction.transaction_code}-${itemIndex}`}
                            key={`${item.product_code}-${itemIndex}`}
                          >
                            <span>{item.product_name} · {item.quantity} × {formatCurrency(item.price)}</span>
                            <strong>{formatCurrency(item.subtotal)}</strong>
                          </p>
                        ))}
                        {!transaction.items.length && (
                          <p className="manager-empty-item" data-testid={`empty-items-${transaction.transaction_code}`}>
                            Detail barang belum tersedia.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {!recentTransactions.length && (
                  <p className="manager-empty-transaction" data-testid="manager-empty-transaction-list">
                    Belum ada transaksi terbaru.
                  </p>
                )}
              </div>
            </section>

            <footer className="manager-update-footer" data-testid="manager-last-updated">
              Pembaruan data: {formatUpdatedAt(overview.stores[0]?.updated_at)}
            </footer>
          </>
        )}
      </section>
    </main>
  );
};